/**
 * Turn-timeout ladder (spec 032 Phase C — T024a / FR-015).
 *
 * The SINGLE source of truth for the turn-critical timeouts that must stay
 * correctly ordered relative to one another. Before this module each value was
 * an inline literal in `n8n-client.ts`, `conversation-gateway.ts`, and the
 * client hooks, so nothing stopped one drifting out of order (finding 0/1 in
 * `contracts/turn-timeout-ladder.md`). The ordering invariant is asserted in
 * `conversation-timeouts.test.ts` — do not reorder these without updating it.
 *
 * PURE CONSTS ONLY — no `process.env`, no imports. This module is consumed by
 * BOTH server (`n8n-client.ts`, `conversation-gateway.ts`) and client
 * (`src/hooks/v2/*`), so it must stay dependency-free and client-bundle-safe
 * (same discipline as `shared/livekit-constants.ts`). The one env-tunable value
 * (the wall-clock budget) keeps its env-reader server-side in
 * `conversation-gateway.ts` (`getTurnWallclockBudgetMs`), which reads the
 * default below — the value lives here, the `process.env` override does not.
 *
 * The invariant (two independent rungs — see `turn-timeout-ladder.md`):
 *
 *   server chain:  stale/no-ping (25s) < per-attempt absolute (60s)
 *                    < CB timeout (65s) < per-turn wall-clock (180s)
 *                    < max session (60m)
 *   client rung:   turn-stream heartbeat (15s) < client stall (45s)
 *
 * NOTE the server chain is NOT joined to the client stall: today
 * `CB timeout (65s) > client stall (45s)` — INTENDED, not a bug. A genuinely
 * alive n8n step running 45–60s would client-error on the 45s stall; the
 * liveness-gated heartbeat (T024a(b)) is what keeps such a turn alive, NOT
 * raising the 45s. The test pins `CB > client-stall` as a known state so a
 * future edit can't "align" them and silently defeat the heartbeat design.
 */

// ── n8n per-attempt liveness (server; see n8n-client.ts createN8nAbortController) ──

/**
 * Abort an in-flight n8n attempt once its liveness signal (MCP tool-call pings,
 * tracked in turn-liveness.ts) has been stale this long. 25s (widened from the
 * silent 10s default in T024a(a)/#1048) exceeds a slow ping-less final
 * generation, and stays below the 45s client stall so a dead turn still
 * surfaces. Same value for the no-signal-yet window.
 */
export const LIVENESS_STALE_SIGNAL_CEILING_MS = 25_000;
export const LIVENESS_NO_SIGNAL_CEILING_MS = 25_000;

/**
 * Hard per-attempt backstop regardless of ping recency — bounds an attempt that
 * (by bug or unusual model behaviour) keeps pinging forever. Must sit ABOVE the
 * stale/no-signal ceilings and BELOW the circuit-breaker timeout.
 */
export const LIVENESS_ABSOLUTE_CEILING_MS = 60_000;

/**
 * opossum circuit-breaker per-call timeout. A redundant OUTER bound that must
 * never fire before the liveness absolute ceiling (60s) — otherwise it clips a
 * turn that's still genuinely alive. Hence 65s > 60s.
 */
export const N8N_CB_TIMEOUT_MS = 65_000;

/**
 * Flat fallback request timeout for an n8n call made WITHOUT a liveness key
 * (no conversation/turn id). Below the liveness ceilings — a non-turn call has
 * no ping stream to wait on.
 */
export const N8N_FLAT_TIMEOUT_MS = 10_000;

// ── per-turn (server; conversation-gateway.ts runTurn) ──

/**
 * Per-turn wall-clock budget bounding leg 1 (the n8n call + step loop) — T024.
 * The env-tunable DEFAULT: `getTurnWallclockBudgetMs` in conversation-gateway.ts
 * reads `CONVERSATION_TURN_WALLCLOCK_BUDGET_MS` and falls back to this. Above the
 * 2-dispatch mode-change worst case and the per-attempt breaker envelope (65s),
 * below the ~10-min pathological product, so valid multi-step turns aren't cut.
 */
export const CONVERSATION_TURN_WALLCLOCK_BUDGET_MS_DEFAULT = 180_000;

/**
 * Turn-stream heartbeat cadence — T024a(b). While n8n liveness is fresh, Cloud
 * emits a heartbeat on the turn stream so the client idle timer re-arms; it
 * STOPS when liveness goes stale/absolute so a dead turn still trips the client
 * stall. Must sit below the client stall (15s < 45s). Consumed in T024a(b).
 */
export const TURN_STREAM_HEARTBEAT_MS = 15_000;

// ── client (src/hooks/v2/*) ──

/**
 * Client turn-stall timeout: if the turn stream is silent this long, the client
 * surfaces a stall/terminal. Shared by text (`useTextConversation`) and voice
 * (`useVoiceConversation`). Unchanged by T024a — the heartbeat re-arms it
 * rather than raising it.
 */
export const CLIENT_TURN_STALL_MS = 45_000;

/**
 * ANA-727 (A1 / R5) — voice: absolute ceiling on a single continuous `thinking`
 * EPISODE (anchored at the first `listening→thinking` of a turn, NOT re-armed by
 * status-label heartbeats). The 45s `CLIENT_TURN_STALL_MS` above is re-armed by
 * every heartbeat, so a concurrent turn's heartbeats can keep it alive
 * indefinitely on a UI wedged on the OLD turn (the 2026-07-15 incident). This is
 * the hard cap that a heartbeat cannot defer: once `thinking` has run this long
 * with no `speaking`/`listening` transition, the client surfaces a retryable
 * error. Set ABOVE the per-turn server wall-clock budget
 * (`CONVERSATION_TURN_WALLCLOCK_BUDGET_MS_DEFAULT` = 180s) so a legitimate
 * max-length turn is never clipped (+30s margin), and BELOW `VOICE_MAX_SESSION_MS`.
 * The agent-side wedge watchdog (`WEDGE_WATCHDOG_MS` = 240s, in agent/src/index.ts,
 * which cannot import this module) sits ABOVE this so the client error fires first.
 * The server owns the env-vs-const invariant via a boot assertion in
 * conversation-gateway.ts (the client cannot read the server's budget env).
 */
export const CLIENT_THINKING_ABSOLUTE_CEILING_MS = 210_000;

/**
 * Voice: absolute max session length before an auto-pause regardless of
 * activity. The top of the ladder.
 */
export const VOICE_MAX_SESSION_MS = 60 * 60 * 1_000;
